import React, { useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';

import LogicSelector from '@components/common/LogicSelector';
import Label from '@components/common/NumberLabel';
import KeyValueBuilder from '@components/mapping-panel/components/RuleBuilder/KeyValueBuilder';
import { Prefix } from '@components/mapping-panel/components/RuleBuilder/RuleGroup/styled';
import {
  DeleteButtonWrapper,
  PlaceholderBox,
  TagKeyValueWrapper,
} from '@components/mapping-panel/components/RuleBuilder/RuleRow/styled';
import { IItemActiveIndices } from '@components/mapping-panel/store';
import { Icon } from '@components/metrics-builder/components/MetricList';
import DeleteActiveSvg from '@icons/mapping-panel/delete-active-icon.svg';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import DragSvg from '@icons/mapping-panel/drag-icon.svg';

import { EInputType } from '../KeyValueBuilder/typed';
import { IRuleGroup, IRuleItem } from '../RuleGroup/typed';

interface IRuleRowProps {
  disabled?: boolean;
  index: number;
  rowLabel: string;
  eventName?: IRuleGroup['eventName'];
  data: IRuleItem;
  showLogicOperator?: boolean;
  itemActiveIndices: IItemActiveIndices | undefined;
  onActive?: () => void;
  onDelete?: () => void;
  onLogicChange?: () => void;
  onChange: (type: EInputType, value: string) => void;
  onKeyValueActiveChange: (type: EInputType, active: boolean) => void;
  style?: React.CSSProperties;
}

const RuleRow: React.FC<IRuleRowProps> = (props) => {
  const {
    disabled,
    index,
    rowLabel,
    eventName,
    data,
    showLogicOperator,
    itemActiveIndices,
    onActive,
    onDelete,
    onLogicChange,
    onChange,
    onKeyValueActiveChange,
    style,
  } = props;

  const [isHover, setHover] = useState<boolean>(false);
  const rowActive = itemActiveIndices?.tagKey || itemActiveIndices?.tagValue;

  const onMouseOver = () => {
    setHover(true);
  };

  const onMouseleave = () => {
    setHover(false);
  };

  const handleRowDeleteClick = (e) => {
    e.stopPropagation();
    onDelete?.();
  };

  const updateActiveRuleRow = (e) => {
    e.stopPropagation();
    onActive?.();
  };

  return (
    <Draggable draggableId={rowLabel} index={index}>
      {(provided, snapshot) => {
        const isDragging = snapshot.isDragging;

        // Restrict dragging to vertical axis
        let transform = provided.draggableProps?.style?.transform;

        if (snapshot.isDragging && transform) {
          // eslint-disable-next-line no-useless-escape
          transform = transform.replace(/\(.+\,/, '(0,');
        }

        const dragStyle = {
          ...provided.draggableProps.style,
          transform,
          ...style,
        };

        return (
          <TagKeyValueWrapper
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
            isDragging={isDragging}
            onMouseOver={onMouseOver}
            onMouseLeave={onMouseleave}
            onClick={updateActiveRuleRow}
            style={dragStyle}
          >
            <Prefix>
              {(isHover || isDragging) && !disabled ? (
                <Icon
                  component={DragSvg}
                  style={{ width: '20px', cursor: 'grab' }}
                  {...provided.dragHandleProps}
                />
              ) : (
                <PlaceholderBox />
              )}
              {showLogicOperator && !disabled ? (
                <LogicSelector value="OR" active={rowActive} onChange={onLogicChange} />
              ) : null}
            </Prefix>
            <Label value={rowLabel} active={rowActive} />
            <KeyValueBuilder
              id={rowLabel}
              disabled={disabled}
              eventName={eventName}
              data={data}
              onChange={onChange}
              active={{ key: itemActiveIndices?.tagKey, value: itemActiveIndices?.tagValue }}
              onActiveChange={onKeyValueActiveChange}
            />
            <DeleteButtonWrapper>
              {isHover && !disabled ? (
                <Icon
                  component={DeleteSvg}
                  activeComponent={DeleteActiveSvg}
                  onClick={handleRowDeleteClick}
                />
              ) : (
                <PlaceholderBox style={{ width: '14px' }} />
              )}
            </DeleteButtonWrapper>
          </TagKeyValueWrapper>
        );
      }}
    </Draggable>
  );
};

export default RuleRow;
